import React, { ReactNode, useState, useMemo, useEffect } from 'react';
import { departamentService, Departament } from '../../api/departament';
import {
  Building2,
  Search,
  Plus,
  Edit2,
  Trash2,
  FileText,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Button,
  Badge,
  Input,
  Label,
  Modal,
  ConfirmModal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

type FormErrors = {
  name?: string;
  description?: string;
};

type FieldWrapperProps = {
  children: ReactNode;
  error?: string;
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
};

const FieldWrapper = ({ children, error, label, htmlFor, required }: FieldWrapperProps) => (
  <div className="flex flex-col gap-1">
    {label ? (
      <Label htmlFor={htmlFor} className="mb-0.5">
        <span className="inline-flex items-center gap-0.5">
          {label}
          {required ? <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span> : null}
        </span>
      </Label>
    ) : null}
    {children}
    {error ? (
      <div className="flex items-center gap-1 text-xs text-rose-600">
        <AlertCircle className="h-3 w-3 flex-shrink-0" />
        <span className="leading-tight">{error}</span>
      </div>
    ) : null}
  </div>
);

type CharCounterProps = { current: number; max: number };
const CharCounter = ({ current, max }: CharCounterProps) => (
  <div
    className={`mt-1 text-[10.5px] font-medium tabular-nums text-right pr-0.5 transition-colors ${
      current >= max ? 'text-rose-500' : current >= max * 0.9 ? 'text-amber-600' : 'text-slate-400'
    }`}
  >
    {current}/{max}
  </div>
);

const DepartamentoPage = () => {
  const [departamentos, setDepartamentos] = useState<Departament[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDepartamento, setEditingDepartamento] = useState<Departament | null>(null);
  const [formData, setFormData] = useState<Partial<Departament>>({
    name: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [departamentoParaExcluir, setDepartamentoParaExcluir] = useState<Departament | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchDepartamentos();
  }, []);

  const fetchDepartamentos = async () => {
    setLoading(true);
    setError(null);
    try {
      const departamentosData = await departamentService.getAll();
      const sorted = Array.isArray(departamentosData)
        ? [...departamentosData].sort((a, b) => b.id - a.id)
        : [];
      setDepartamentos(sorted);
    } catch {
      setError('Erro ao carregar departamentos. Tente novamente mais tarde.');
      setDepartamentos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};
    const name = formData.name?.trim() ?? '';
    const description = formData.description?.trim() ?? '';

    if (!name) {
      errors.name = 'Informe o nome do departamento';
    } else if (name.length < 2) {
      errors.name = 'Nome muito curto (mínimo 2 caracteres)';
    } else if (name.length > 100) {
      errors.name = 'Nome excede o limite de 100 caracteres';
    }

    const nomeDuplicado = departamentos.find(
      (d) => d.name.trim().toLowerCase() === name.toLowerCase() && d.id !== editingDepartamento?.id,
    );
    if (nomeDuplicado) {
      errors.name = 'Já existe um departamento com este nome';
    }

    if (description && description.length > 500) {
      errors.description = 'Descrição excede o limite de 500 caracteres';
    }

    return errors;
  };

  const handleCreate = () => {
    setEditingDepartamento(null);
    setFormData({ name: '', description: '' });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (departamento: Departament) => {
    setEditingDepartamento(departamento);
    setFormData({
      name: departamento.name,
      description: departamento.description || '',
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const resetModal = () => {
    setIsModalOpen(false);
    setEditingDepartamento(null);
    setFormData({ name: '', description: '' });
    setFormErrors({});
  };

  const abrirConfirmacaoExclusao = (departamento: Departament) => {
    setDepartamentoParaExcluir(departamento);
    setIsDeleteConfirmOpen(true);
  };
  const cancelarExclusao = () => {
    if (deleteLoading) return;
    setIsDeleteConfirmOpen(false);
    setDepartamentoParaExcluir(null);
  };
  const confirmarExclusao = async () => {
    if (!departamentoParaExcluir) return;
    setDeleteLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await departamentService.delete(departamentoParaExcluir.id);
      await fetchDepartamentos();
      setSuccessMessage('Departamento excluído com sucesso.');
      cancelarExclusao();
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Erro ao excluir departamento');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      if (editingDepartamento) {
        await departamentService.update(editingDepartamento.id, {
          name: formData.name || '',
          description: formData.description || '',
        });
      } else {
        await departamentService.create({
          name: formData.name || '',
          description: formData.description || '',
        });
      }
      resetModal();
      await fetchDepartamentos();
      setSuccessMessage(
        editingDepartamento ? 'Departamento atualizado com sucesso.' : 'Departamento cadastrado com sucesso.',
      );
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Erro ao salvar departamento');
    } finally {
      setLoading(false);
    }
  };

  const departamentosFiltrados = useMemo(() => {
    if (!Array.isArray(departamentos)) return [];
    if (!searchTerm.trim()) return departamentos;
    const term = searchTerm.toLowerCase().trim();
    return departamentos.filter(
      (d) =>
        d?.name?.toLowerCase().includes(term) ||
        (d?.description && d.description.toLowerCase().includes(term)),
    );
  }, [departamentos, searchTerm]);

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(departamentosFiltrados.length / ITEMS_PER_PAGE)),
    [departamentosFiltrados.length],
  );

  const { departamentosPaginados, pageRange } = useMemo(() => {
    const total = departamentosFiltrados.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      departamentosPaginados: departamentosFiltrados.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [departamentosFiltrados, currentPage, totalPaginas]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [departamentosFiltrados.length, totalPaginas, currentPage]);

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 3500);
    return () => clearTimeout(t);
  }, [successMessage]);

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Departamentos" hideTitleInBody />

        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[15px] font-semibold text-slate-500">
              Organize as áreas internas da sua operação
            </p>
            <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold tracking-tight text-slate-900 leading-tight">
              Cadastre, edite e remova departamentos.
              <span className="text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                {' '}Vincule colaboradores e órgãos para organizar a frota.
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="primary"
              onClick={handleCreate}
              disabled={loading}
            >
              <Plus className="h-4 w-4 stroke-[1.8]" />
              Novo departamento
            </Button>
          </div>
        </div>

        {successMessage ? (
          <div className="mb-5 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50/50 px-4.5 py-3.5 text-[13px] font-medium text-emerald-800 shadow-[0_2px_8px_-6px_rgba(16,185,129,0.25)]">
            {successMessage}
          </div>
        ) : null}
        {error ? (
          <div className="mb-5 rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50 via-white to-rose-50/50 px-4.5 py-3.5 text-[13px] font-medium text-rose-800 shadow-[0_2px_8px_-6px_rgba(244,63,94,0.25)]">
            {error}
          </div>
        ) : null}

        <SectionCard>
          <CardHeader>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4 w-full">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-500 text-white shadow-[0_6px_16px_-8px_rgba(59,130,246,0.6)]">
                    <Building2 className="h-[18px] w-[18px] stroke-[1.9]" />
                  </div>
                  <h2 className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                    Departamentos cadastrados
                  </h2>
                </div>
                <p className="text-[13px] text-slate-500 mt-1.5">
                  {loading
                    ? 'Carregando registros...'
                    : pageRange.total === 0
                      ? 'Nenhum departamento encontrado'
                      : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} departamentos listados (total: ${departamentos.length})`}
                </p>
              </div>
              <div className="w-full lg:max-w-md">
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 h-4.5 w-4.5 -translate-y-1/2 stroke-[1.8] text-slate-400" />
                  <Input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por nome ou descrição..."
                    className="!pl-11"
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            <ModernTable className="table-fixed layout-fixed">
              <Thead>
                <Tr>
                  <Th className="w-[22%] min-w-[200px] max-w-[360px]">Departamento</Th>
                  <Th className="w-[36%] min-w-[240px] max-w-[520px]">Descrição</Th>
                  <Th className="w-[13%] min-w-[130px] max-w-[170px]">Criado em</Th>
                  <Th className="w-[13%] min-w-[130px] max-w-[170px]">Atualizado em</Th>
                  <Th className="!w-[120px] !min-w-[120px] !max-w-[150px]">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {departamentosFiltrados.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-5 py-14 text-center text-sm text-slate-500"
                    >
                      {loading
                        ? 'Carregando...'
                        : searchTerm
                          ? 'Nenhum departamento corresponde à busca.'
                          : 'Nenhum departamento encontrado'}
                    </td>
                  </tr>
                ) : (
                  departamentosPaginados.map((departamento) => {
                    const dataCriacao = new Date(departamento.created_at).toLocaleDateString('pt-BR');
                    const dataAtualizacao = new Date(departamento.updated_at).toLocaleDateString('pt-BR');
                    return (
                    <Tr key={departamento.id}>
                      <Td className="max-w-[360px]">
                        <div className="min-w-0 w-full max-w-full overflow-hidden">
                          <div className="flex items-center gap-3 min-w-0 w-full max-w-full">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-sky-500 via-blue-500 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(59,130,246,0.6)]">
                              <Building2 className="h-[18px] w-[18px] stroke-[1.9]" />
                            </div>
                            <div className="min-w-0 flex-1 max-w-full">
                              <div
                                className="truncate font-semibold text-slate-900 w-full"
                                title={departamento.name}
                              >
                                {departamento.name}
                              </div>
                              <div
                                className="mt-0.5 text-[12px] text-slate-500 truncate w-full"
                                title={`ID ${departamento.id}`}
                              >
                                ID {departamento.id}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Td>
                      <Td className="max-w-[520px]">
                        <div className="min-w-0 w-full max-w-full overflow-hidden">
                          {departamento.description ? (
                            <span
                              className="block truncate font-medium text-slate-700 w-full"
                              title={departamento.description}
                            >
                              {departamento.description}
                            </span>
                          ) : (
                            <Badge tone="slate" className="!text-[12px]">
                              Sem descrição
                            </Badge>
                          )}
                        </div>
                      </Td>
                      <Td className="max-w-[170px]">
                        <div className="min-w-0 w-full max-w-full overflow-hidden">
                          <span
                            className="block truncate font-medium text-slate-700 tabular-nums whitespace-nowrap w-full"
                            title={dataCriacao}
                          >
                            {dataCriacao}
                          </span>
                        </div>
                      </Td>
                      <Td className="max-w-[170px]">
                        <div className="min-w-0 w-full max-w-full overflow-hidden">
                          <span
                            className="block truncate font-medium text-slate-700 tabular-nums whitespace-nowrap w-full"
                            title={dataAtualizacao}
                          >
                            {dataAtualizacao}
                          </span>
                        </div>
                      </Td>
                      <Td className="!max-w-[150px]">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleEdit(departamento)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                            title="Editar departamento"
                            aria-label="Editar departamento"
                          >
                            <Edit2 className="h-[15px] w-[15px] stroke-[1.8]" />
                          </button>
                          <button
                            type="button"
                            onClick={() => abrirConfirmacaoExclusao(departamento)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                            title="Excluir departamento"
                            aria-label="Excluir departamento"
                          >
                            <Trash2 className="h-[15px] w-[15px] stroke-[1.8]" />
                          </button>
                        </div>
                      </Td>
                    </Tr>
                    );
                  })
                )}
              </Tbody>
            </ModernTable>

            {pageRange.total > 0 ? (
              <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[13px] font-medium text-slate-500">
                  Mostrando{' '}
                  <span className="font-semibold text-slate-700">
                    {pageRange.start}-{pageRange.end}
                  </span>{' '}
                  de{' '}
                  <span className="font-semibold text-slate-700">{pageRange.total}</span>{' '}
                  resultados
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    aria-label="Primeira página"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                  >
                    <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Página anterior"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                  >
                    <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                  </button>
                  <div className="mx-1 inline-flex items-center gap-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span
                          key={`dots-${idx}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          aria-label={`Ir para página ${n}`}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                            n === currentPage
                              ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                              : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                          }`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    aria-label="Próxima página"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                  >
                    <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    aria-label="Última página"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                  >
                    <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>

        {isModalOpen && (
          <Modal
            onClose={resetModal}
            maxWidthClass="max-w-2xl"
            title={
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-600 text-white shadow-[0_6px_16px_-8px_rgba(59,130,246,0.65)]">
                  <Building2 className="h-[18px] w-[18px]" />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold tracking-tight text-slate-900 leading-tight">
                    {editingDepartamento ? 'Editar departamento' : 'Novo departamento'}
                  </h3>
                  <p className="text-[12px] font-medium text-slate-500 mt-0.5 leading-snug">
                    {editingDepartamento
                      ? 'Atualize os dados de um departamento existente'
                      : 'Crie um novo departamento para organizar sua operação'}
                  </p>
                </div>
              </div>
            }
            footer={
              <>
                <Button type="button" variant="secondary" onClick={resetModal} disabled={loading}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  form="dept-form"
                  variant="primary"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                      Salvando...
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      {editingDepartamento ? (
                        <Edit2 className="h-4 w-4 stroke-[1.8]" />
                      ) : (
                        <Plus className="h-4 w-4 stroke-[1.8]" />
                      )}
                      {editingDepartamento ? 'Salvar alterações' : 'Cadastrar departamento'}
                    </span>
                  )}
                </Button>
              </>
            }
          >
            <form
              id="dept-form"
              onSubmit={(e) => void handleSubmit(e)}
              className="flex flex-col gap-6"
              noValidate
            >
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <div className="h-7 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-sky-500 via-blue-500 to-indigo-500" />
                  <h4 className="text-[13.5px] font-bold tracking-tight text-slate-800 uppercase">
                    Dados do departamento
                  </h4>
                </div>

                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                      Nome do departamento
                    </span>
                  }
                  required
                  htmlFor="dept-name"
                  error={formErrors.name}
                >
                  <Input
                    id="dept-name"
                    name="name"
                    type="text"
                    value={formData.name || ''}
                    onChange={handleInputChange}
                    onBlur={() =>
                      setFormErrors((prev) => ({
                        ...prev,
                        name: validateForm().name,
                      }))
                    }
                    placeholder="Ex: Recursos Humanos, Logística, Financeiro..."
                    maxLength={100}
                    aria-invalid={!!formErrors.name}
                  />
                  <CharCounter current={(formData.name || '').length} max={100} />
                </FieldWrapper>

                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                      Descrição (opcional)
                    </span>
                  }
                  htmlFor="dept-description"
                  error={formErrors.description}
                >
                  <textarea
                    id="dept-description"
                    name="description"
                    rows={4}
                    value={formData.description || ''}
                    onChange={handleInputChange}
                    onBlur={() =>
                      setFormErrors((prev) => ({
                        ...prev,
                        description: validateForm().description,
                      }))
                    }
                    className="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-[13.5px] text-slate-900 shadow-sm placeholder:text-slate-400 transition-colors focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/30 resize-none"
                    placeholder="Descreva brevemente a finalidade, responsabilidades ou áreas abrangidas por este departamento..."
                    maxLength={500}
                    aria-invalid={!!formErrors.description}
                  />
                  <CharCounter current={(formData.description || '').length} max={500} />
                </FieldWrapper>
              </div>
            </form>
          </Modal>
        )}

        <ConfirmModal
          isOpen={isDeleteConfirmOpen}
          title={
            departamentoParaExcluir
              ? `Excluir ${departamentoParaExcluir.name}?`
              : 'Excluir departamento?'
          }
          description={
            departamentoParaExcluir ? (
              <span>
                Você está prestes a excluir{' '}
                <strong className="font-semibold text-slate-700">
                  {departamentoParaExcluir.name}
                </strong>
                . Esta ação é irreversível e pode impactar colaboradores e órgãos vinculados.
              </span>
            ) : (
              <span>
                Esta ação é irreversível e pode impactar colaboradores e órgãos vinculados.
              </span>
            )
          }
          confirmLabel={
            deleteLoading ? (
              <span className="inline-flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                Excluindo...
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Trash2 className="h-4 w-4 stroke-[1.8]" />
                Sim, excluir departamento
              </span>
            )
          }
          cancelLabel="Cancelar"
          confirmVariant="danger"
          onConfirm={confirmarExclusao}
          onCancel={cancelarExclusao}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default DepartamentoPage;
